import React from 'react';
import { motion } from 'framer-motion';
import type { PracticeInfo } from '../../api/bookingApi';

// The v2 flow's persistent chrome. On phones both bars are sticky (the page is
// the only scroller); from `sm` up they sit statically inside the glass card.
// Harness contract: the counter keeps its visible "{n} / {total}" text and
// data-step-index/-total attributes; the footer holds the ONLY button.btn-primary
// (data-testid step-next) and a button whose text is exactly "Back" (step-back).
// No h2/h3, no <label>, no .space-y-6 in here — fillStep reads those from steps.

export function V2ProgressHeader({ practiceInfo, current, total, progress, stageLabel, stagePos, stageSize, reducedMotion }: {
  practiceInfo?: PracticeInfo | null;
  current: number;
  total: number;
  progress: number;
  /** Human-readable journey stage ("Medical history") shown above the raw counter. */
  stageLabel?: string;
  /** This screen's place within the stage; the "· X of Y" is shown only when the stage spans more than one screen. */
  stagePos?: number;
  stageSize?: number;
  reducedMotion: boolean;
}) {
  const stageText = stageLabel
    ? stageSize && stageSize > 1 ? `${stageLabel} · ${stagePos} of ${stageSize}` : stageLabel
    : null;
  return (
    <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-100 pt-[env(safe-area-inset-top)] sm:static sm:bg-transparent sm:backdrop-blur-0 sm:border-0 sm:pt-0">
      <div className="flex items-center justify-between gap-3 px-5 py-3 sm:px-0 sm:py-0 sm:pb-3">
        <div className="flex items-center gap-2 min-w-0 sm:invisible">
          {practiceInfo?.logo_url ? (
            <img src={practiceInfo.logo_url} alt={practiceInfo.name} className="h-6 object-contain" />
          ) : (
            <span className="text-primary-deep font-extrabold text-sm tracking-tight uppercase truncate">
              {practiceInfo?.name || ''}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end shrink-0 leading-tight">
          {stageText && (
            <span data-testid="step-stage" className="text-xs font-bold text-primary-deep tracking-tight truncate max-w-[60vw] sm:max-w-none">
              {stageText}
            </span>
          )}
          <div
            data-testid="step-counter"
            data-step-index={current}
            data-step-total={total}
            className="text-[10px] font-bold text-gray-500 uppercase tracking-widest"
          >
            {current} / {total}
          </div>
        </div>
      </div>
      <div className="h-[3px] bg-gray-100 overflow-hidden sm:rounded-full">
        <motion.div
          className="h-full bg-primary"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: reducedMotion ? 0.01 : 0.25 }}
        />
      </div>
    </header>
  );
}

export interface V2Notice {
  text: string;
  action?: { label: string; accessibleName?: string; disabled: boolean; onClick: () => void };
}

export function V2FooterBar({ onBack, backDisabled, error, notices = [], primaryLabel, primaryDisabled, onPrimary }: {
  onBack: () => void;
  backDisabled: boolean;
  error: string;
  /** Insurance-check messages for this screen (not errors: the patient may continue). */
  notices?: V2Notice[];
  primaryLabel: string;
  primaryDisabled: boolean;
  onPrimary: () => void;
}) {
  return (
    <footer
      data-testid="step-footer"
      className="sticky bottom-0 z-20 bg-white/95 backdrop-blur border-t border-gray-100 px-5 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] sm:static sm:bg-transparent sm:backdrop-blur-0 sm:border-0 sm:px-0 sm:pt-8 sm:pb-0"
    >
      {error && <p className="text-red-500 text-sm font-medium mb-2">{error}</p>}
      {/* Always mounted, so a message added to it is announced. */}
      <div role="status" data-testid="step-notices">
        {notices.map((notice, i) => (
          <div key={i} className="flex items-center gap-3 mb-2">
            <p className="text-amber-800 text-sm font-medium">{notice.text}</p>
            {notice.action && (
              <button type="button" aria-label={notice.action.accessibleName} disabled={notice.action.disabled}
                onClick={notice.action.onClick} className="shrink-0 text-sm font-semibold text-primary-deep underline disabled:opacity-50">
                {notice.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button
          data-testid="step-back"
          onClick={onBack}
          disabled={backDisabled}
          className={`shrink-0 flex items-center gap-2 h-12 px-3 -ml-3 text-sm font-semibold transition-colors ${
            backDisabled ? 'text-gray-300 pointer-events-none' : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        {/* .btn-primary's brand cyan is 2.6:1 under white text; the deeper
            shade clears WCAG AA (4.95:1) for this 14px label. */}
        <button
          data-testid="step-next"
          onClick={onPrimary}
          disabled={primaryDisabled}
          className="btn-primary flex-1 bg-primary-deep hover:bg-primary-deeper shadow-[0_4px_14px_0_rgba(22,122,148,0.35)] hover:shadow-[0_6px_20px_rgba(22,122,148,0.25)]"
        >
          {primaryLabel}
        </button>
      </div>
    </footer>
  );
}
